import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { useGetExhibitionQuery, useGetCountriesQuery, useCreateExhibitionMutation, useUpdateExhibitionMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ImageUpload from '../../../components/ImageUpload';
import TagInput from '../../../components/TagInput';
import { showSuccess, showError } from '../../../utils/toast';
import { useLanguage } from '../../../context/LanguageContext';

const AdminExhibitionForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  
  const { data, isLoading } = useGetExhibitionQuery(id!, { skip: !isEdit });
  const { data: countriesData } = useGetCountriesQuery({});
  const [createExhibition, { isLoading: isCreating }] = useCreateExhibitionMutation();
  const [updateExhibition, { isLoading: isUpdating }] = useUpdateExhibitionMutation();

  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    note: '',
    note_ar: '',
    enterance_fee: '',
    booking_link: '',
    exhibition_link: '',
    country: '',
    location: '',
    location_ar: '',
    exhibition_at: '',
    description: '',
    description_ar: '',
    tags: [] as string[],
    tags_ar: [] as string[],
    image: null as File | null,
  });

  const [existingImage, setExistingImage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isEdit && data?.data) {
      const exhibition = data.data;
      setFormData({
        title: exhibition.title || '',
        title_ar: exhibition.title_ar || '',
        note: exhibition.note || '',
        note_ar: exhibition.note_ar || '',
        enterance_fee: exhibition.enterance_fee || '',
        booking_link: exhibition.booking_link || '',
        exhibition_link: exhibition.exhibition_link || '',
        country: exhibition.country || '',
        location: exhibition.location || '',
        location_ar: exhibition.location_ar || '',
        exhibition_at: exhibition.exhibition_at ? new Date(exhibition.exhibition_at).toISOString().slice(0, 16) : '',
        description: exhibition.description || '',
        description_ar: exhibition.description_ar || '',
        tags: exhibition.tags ? (typeof exhibition.tags === 'string' ? exhibition.tags.split(',').filter(Boolean) : exhibition.tags) : [],
        tags_ar: exhibition.tags_ar ? (typeof exhibition.tags_ar === 'string' ? exhibition.tags_ar.split(',').filter(Boolean) : exhibition.tags_ar) : [],
        image: null,
      });
      if (exhibition.image) {
        const imageFilename = exhibition.image.includes('/uploads/') 
          ? exhibition.image.split('/uploads/')[1] 
          : exhibition.image;
        setExistingImage(imageFilename);
      }
    }
  }, [isEdit, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (content: string) => {
    setFormData(prev => ({ ...prev, description: content }));
  };

  const handleContentArChange = (content_ar: string) => {
    setFormData(prev => ({ ...prev, description_ar: content_ar }));
  };

  const handleTagsChange = (tags: string[]) => {
    setFormData(prev => ({ ...prev, tags }));
  };

  const handleTagsArChange = (tags_ar: string[]) => {
    setFormData(prev => ({ ...prev, tags_ar }));
  };

  const handleImageChange = (file: File | null) => {
    setFormData(prev => ({ ...prev, image: file }));
  };

  const handleRemoveExistingImage = () => {
    setExistingImage(undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('title_ar', formData.title_ar || '');
      submitData.append('note', formData.note || '');
      submitData.append('note_ar', formData.note_ar || '');
      submitData.append('enterance_fee', formData.enterance_fee);
      submitData.append('booking_link', formData.booking_link || '');
      submitData.append('exhibition_link', formData.exhibition_link || '');
      submitData.append('country', formData.country);
      submitData.append('location', formData.location || '');
      submitData.append('location_ar', formData.location_ar || '');
      submitData.append('exhibition_at', formData.exhibition_at);
      submitData.append('description', formData.description);
      submitData.append('description_ar', formData.description_ar || '');
      submitData.append('tags', formData.tags.join(','));
      submitData.append('tags_ar', formData.tags_ar.join(','));
      
      if (formData.image) {
        submitData.append('image', formData.image);
      }

      if (isEdit) {
        const result = await updateExhibition({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || t('Exhibition updated successfully!'));
      } else {
        const result = await createExhibition(submitData).unwrap();
        showSuccess(result.message || t('Exhibition created successfully!'));
      }
      
      navigate('/admin/exhibitions');
    } catch (err: any) {
      showError(err?.data?.message || t(isEdit ? 'Failed to update exhibition. Please try again.' : 'Failed to create exhibition. Please try again.'));
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  const countries = countriesData?.data?.data || [];

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? t('Edit Exhibition') : t('Add New Exhibition')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Dashboard')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/exhibitions'); }} className="text-muted text-hover-primary">{t('Exhibitions')}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 my-3">
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Title')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        required
                        placeholder={t('Enter Exhibition Title')}
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Title Arabic')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Exhibition Title in Arabic')}
                        name="title_ar"
                        value={formData.title_ar}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Note')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Exhibition Note')}
                        name="note"
                        value={formData.note}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Note Arabic')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Exhibition Note in Arabic')}
                        name="note_ar"
                        value={formData.note_ar}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Entrance Fee')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        required
                        placeholder={t('Enter Exhibition Entrance Fee')}
                        name="enterance_fee"
                        value={formData.enterance_fee}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Booking Link (optional)')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Exhibition Booking link if its paid entrance')}
                        name="booking_link"
                        value={formData.booking_link}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Exhibition Link')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Exhibition link')}
                        name="exhibition_link"
                        value={formData.exhibition_link}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Country')}</label>
                      <select
                        name="country"
                        required
                        className="form-control form-control-solid"
                        value={formData.country}
                        onChange={handleInputChange}
                      >
                        <option value="">{t('Select Country')}</option>
                        {countries.map((country: any) => (
                          <option key={country.id} value={country.name}>{country.name} ({country.name_ar})</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Location')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Exhibition Location')}
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Location Ar')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Exhibition Location Arabic')}
                        name="location_ar"
                        value={formData.location_ar}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Exhibition At')}</label>
                      <input
                        type="datetime-local"
                        required
                        className="form-control form-control-solid"
                        name="exhibition_at"
                        value={formData.exhibition_at}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <ImageUpload
                        existingImage={existingImage}
                        onImageChange={handleImageChange}
                        onRemoveExisting={handleRemoveExistingImage}
                        label={t('Upload Picture')}
                        name="image"
                      />
                    </div>
                    
                    <div className="col-md-12 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Tags')}</label>
                      <TagInput
                        value={formData.tags}
                        onChange={handleTagsChange}
                        placeholder={t('Write tags and press enter')}
                        className="form-control"
                      />
                    </div>
                    
                    <div className="col-md-12 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Tags Ar')}</label>
                      <TagInput
                        value={formData.tags_ar}
                        onChange={handleTagsArChange}
                        placeholder={t('Write arabic tags and press enter')}
                        className="form-control"
                      />
                    </div>
                    
                    <div className="col-md-12 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Description')}</label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={formData.description}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          handleContentChange(data);
                        }}
                        config={{
                          toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'blockQuote', 'insertTable', '|', 'undo', 'redo'],
                        }}
                      />
                    </div>
                    
                    <div className="col-md-12 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Description Arabic')}</label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={formData.description_ar}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          handleContentArChange(data);
                        }}
                        config={{
                          toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'blockQuote', 'insertTable', '|', 'undo', 'redo'],
                          language: 'ar',
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <button type="button" id="kt_modal_new_target_cancel" className="btn btn-light me-3" onClick={() => navigate('/admin/exhibitions')}>
                      {t('Back')}
                    </button>
                    <button type="submit" id="kt_modal_new_target_submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">{t('Submit')}</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          {t('Please wait...')}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminExhibitionForm;
